import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { CheckCircle2, ChevronDown, ChevronUp, Info } from "lucide-react";

interface Props {
  /** Show the panel expanded by default (e.g. right after connecting Google). */
  defaultOpen?: boolean;
}

const GOOGLE_MEET_ADMIN_URL =
  "https://admin.google.com/ac/apps/meet/settings";
const GOOGLE_MEET_HELP_URL =
  "https://support.google.com/meet/answer/9308630";

export default function GoogleMeetRequirements({ defaultOpen = false }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);

  const requirements: string[] = t(
    "scheduler.settings.googleMeetAI.requirements",
    { returnObjects: true }
  ) as string[];

  return (
    <div className="rounded-xl border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-green-700 dark:text-green-300">
          <Info size={15} className="shrink-0" />
          {t("scheduler.settings.googleMeetAI.title")}
        </span>
        {open ? (
          <ChevronUp size={15} className="shrink-0 text-green-500" />
        ) : (
          <ChevronDown size={15} className="shrink-0 text-green-500" />
        )}
      </button>

      {open && (
        <div className="border-t border-green-200 px-4 pb-4 pt-3 dark:border-green-800">
          <p className="mb-3 text-xs text-green-700 dark:text-green-300">
            {t("scheduler.settings.googleMeetAI.description")}
          </p>

          <ul className="flex flex-col gap-2">
            {Array.isArray(requirements) &&
              requirements.map((_, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-green-800 dark:text-green-200">
                  <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-green-500" />
                  <span>
                    <Trans
                      i18nKey={`scheduler.settings.googleMeetAI.requirements.${i}`}
                      components={{ strong: <strong /> }}
                    />
                  </span>
                </li>
              ))}
          </ul>

          <div className="mt-4 flex flex-wrap gap-3">
            <a
              href={GOOGLE_MEET_ADMIN_URL}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-medium text-green-600 underline dark:text-green-400"
            >
              {t("scheduler.settings.googleMeetAI.linkAdmin")}
            </a>
            <a
              href={GOOGLE_MEET_HELP_URL}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-medium text-green-600 underline dark:text-green-400"
            >
              {t("scheduler.settings.googleMeetAI.linkHelp")}
            </a>
          </div>

          <p className="mt-3 text-xs text-green-600 dark:text-green-400">
            {t("scheduler.settings.googleMeetAI.outcome")}
          </p>
        </div>
      )}
    </div>
  );
}
